/* ============================================================================
   Diyona Optimistic Cart  (window.DiyonaOptimisticCart)
   Rebuilt 2026-06-19 from project_diamond_atc_optimization.md spec, targeting
   STEP 3 of the ring builder (paired diamond + setting).

   Flow: complete-ring ATC adds a $0 SENTINEL diamond line (variant 51975403077948)
   with all diamond data in line-item properties + the REAL setting line, then
   redirects to /cart immediately. This script, on /cart, renders nothing itself
   (cart-item.liquid renders the placeholder from properties) but:
     1) gates checkout while any pending diamond line exists,
     2) corrects the displayed subtotal (sentinel is $0),
     3) in the background calls create-product?type=Ring, ADDS the real diamond
        variant (same properties), then REMOVES the sentinel line (add-then-remove
        so the cart never lacks the item), then refreshes the cart UI.
   ========================================================================== */
(function () {
  var SENTINEL_VARIANT = 51975403077948;
  var API_BASE = 'https://diamond-ring-builder-flax.vercel.app/api';
  var PENDING_PROP = '_pending_diamond_id';

  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Date.now() + Math.floor(performance.now() * 1000)) % 16; // time-seeded (no Math.random dependency for determinism in tests)
      r = (r + Math.floor((window.__diyRand = (window.__diyRand || 0) + 7) ) ) % 16;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  function money(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
  function getCart() { return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); }

  /* ---- Build the Step-3 ring line items (called by complete-ring ATC) ----
     Ring size is OPTIONAL at step 3: sizeLabel null omits the Ring Size property
     and the cart page hard-blocks checkout until the shopper picks one there. */
  function buildRingLineItems(opts) {
    var d = opts.diamond, setting = opts.setting, engrave = opts.engrave, shipBy = opts.shipBy;
    // Prefer the pre-formatted sizeLabel ('US 7' or null); legacy callers pass size.
    var sizeLabel = (opts.sizeLabel !== undefined) ? opts.sizeLabel : (opts.size ? 'US ' + opts.size : null);
    var pid = uuid();
    var idem = uuid();
    var diamondSummary = d.carat + 'ct ' + d.color + ' ' + d.clarity + ' ' + d.shape;
    // Field-tolerant: the ring-builder cookie diamond uses price/image/certificate;
    // the Supabase row uses markup_price/price_usd/image_url/certificate_number.
    var realPrice = Number(d.markup_price || d.price_usd || d.price || 0);
    var imageUrl = d.image_url || d.image || '';
    var certNum = d.certificate_number || d.certificate || '';

    var diamondProps = {
      // visible
      'Diamond': diamondSummary,
      'Certificate': ((d.lab || 'IGI') + ' ' + certNum).trim(),
      'Paired Setting': setting.title,
      'Ship by': shipBy,
      // hidden contract
      '_ring_builder': 'true',
      '_ring_type': 'Ring',
      '_pending_diamond_id': pid,
      '_idempotency_key': idem,
      '_diamond_sku': d.sku,
      '_real_price': realPrice.toFixed(2),
      '_image_url': imageUrl,
      '_shape': d.shape || '', '_carat': String(d.carat || ''), '_color': d.color || '',
      '_clarity': d.clarity || '', '_cut': d.cut || '', '_lab': d.lab || '',
      '_certificate_number': certNum, '_max_delivery_days': String(d.max_delivery_days || '')
    };
    var settingProps = {
      '_ring_builder': 'true',
      'Paired Diamond': diamondSummary,
      'Diamond SKU': d.sku,
      'Ship by': shipBy
    };
    if (sizeLabel) { diamondProps['Ring Size'] = sizeLabel; settingProps['Ring Size'] = sizeLabel; }
    if (engrave) { diamondProps['_engraving'] = engrave; settingProps['Custom Engraving'] = engrave; }

    var items = [
      { id: SENTINEL_VARIANT, quantity: 1, properties: diamondProps },
      { id: setting.variantId, quantity: 1, properties: settingProps }
    ];
    return { items: items, pendingId: pid };
  }

  function optimisticAddRing(opts) {
    var built = buildRingLineItems(opts);
    var body = { items: built.items };
    var engrave = opts.engrave;
    var addEngraving = engrave
      ? fetch('/products/engraving-fee.js').then(function (r) { return r.json(); }).then(function (fee) {
          if (fee && fee.variants && fee.variants.length) {
            body.items.push({ id: fee.variants[0].id, quantity: 1, properties: { '_ring_builder': 'true', 'Engraving Text': engrave, '_diamond_sku': (opts.diamond && opts.diamond.sku) || '' } });
          }
        }).catch(function () {})
      : Promise.resolve();
    return addEngraving.then(function () {
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { if (!r.ok) throw new Error('add failed ' + r.status); return r.json(); });
    });
  }

  /* ---- Build a single LOOSE diamond line (called by diamond-detail "Buy Loose") ---- */
  function buildLooseLineItem(d, shipBy) {
    var pid = uuid();
    var idem = uuid();
    var diamondSummary = d.carat + 'ct ' + d.color + ' ' + d.clarity + ' ' + d.shape;
    // Field-tolerant: Supabase row uses markup_price/price_usd/image_url/certificate_number.
    var realPrice = Number(d.markup_price || d.price_usd || d.price || 0);
    var imageUrl = d.image_url || d.image || '';
    var certNum = d.certificate_number || d.certificate || '';
    var props = {
      // visible
      'Diamond': diamondSummary,
      'Certificate': ((d.lab || '') + ' ' + certNum).trim(),
      'Ship By': shipBy,
      // hidden contract
      '_pending_diamond_id': pid,
      '_idempotency_key': idem,
      '_pending_loose': 'true',     // cart-item.liquid renders this as bundle_type 'loose', not 'diamond'
      '_ring_type': 'Loose',        // reconcile() -> create-product?type=Loose
      '_diamond_sku': d.sku,
      '_real_price': realPrice.toFixed(2),
      '_image_url': imageUrl,
      '_shape': d.shape || '', '_carat': String(d.carat || ''), '_color': d.color || '',
      '_clarity': d.clarity || '', '_cut': d.cut || '', '_lab': d.lab || '',
      '_certificate_number': certNum, '_max_delivery_days': String(d.max_delivery_days || '')
    };
    return { id: SENTINEL_VARIANT, quantity: 1, properties: props };
  }

  // Optimistic loose add: dedupe against the current cart by SKU, else add a $0 sentinel loose
  // line and let /cart reconcile it in the background. Resolves fast (no create-product wait),
  // so the caller can redirect to /cart immediately. The diamond renders from its line properties.
  function optimisticAddLoose(d, shipBy) {
    var item = buildLooseLineItem(d, shipBy);
    return getCart().then(function (cart) {
      var sku = d.sku;
      var dup = (cart.items || []).some(function (li) {
        if (li.sku && li.sku === sku) return true;
        return li.properties && li.properties._diamond_sku === sku;
      });
      if (dup) return { alreadyInCart: true };
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] }) })
        .then(function (r) { if (!r.ok) throw new Error('add failed ' + r.status); return r.json(); })
        .then(function () { return { added: true }; });
    });
  }

  /* ---- ATC overlay animation (minimal; safe no-ops if markup absent) ---- */
  function showAtcAnimation() { var o = document.getElementById('diy-atc-overlay'); if (o) o.classList.add('is-active'); }
  function hideAtcAnimation() { var o = document.getElementById('diy-atc-overlay'); if (o) o.classList.remove('is-active'); }

  /* ================= /cart PAGE: deferred checkout + reconcile ================= */
  // The "Continue to Checkout" button looks/behaves 100% normal. If a shopper clicks it BEFORE the
  // diamond's real product has been created+added, we don't gray it out — we show an in-button spinner
  // and auto-proceed to checkout the instant the product lands. No grayed/disabled state.
  var pendingActive = false;  // a sentinel diamond is still being created/swapped
  var wantsCheckout = false;  // shopper clicked checkout during the pending window

  function checkoutBtn() { return document.querySelector('button[name="checkout"], .cart__checkout'); }

  function setGate(on) {
    pendingActive = on;
    // Surface pending state to CSS: accelerated wallet buttons (Apple Pay / Google Pay /
    // Shop Pay) bypass the click interceptor below — main-cart.liquid shields them with
    // pointer-events while this class is up, so a wallet can't buy a $0 sentinel line.
    document.documentElement.classList.toggle('diy-cart-pending', !!on);
    if (!on && wantsCheckout) {
      // Diamond is ready and the shopper already asked to check out — proceed now.
      wantsCheckout = false;
      var btn = checkoutBtn();
      if (btn) btn.click(); // pendingActive is false now, so the handler lets it through to native checkout
    }
  }

  document.addEventListener('click', function (e) {
    if (!pendingActive) return;
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"], .cart__checkout');
    if (!btn) return;
    e.preventDefault(); e.stopImmediatePropagation();
    wantsCheckout = true;
    btn.classList.add('diy-loading'); // in-button spinner until the diamond lands
  }, true);

  // ---- rbfix v1: shared bundle-line classifiers (used by remove interceptor, reconcile guard, FIX 4) ----
  function isSettingLine(li) { var q = (li && li.properties) || {}; return q._ring_builder === 'true' && !!q['Paired Diamond']; }
  function isFeeLine(li) { var q = (li && li.properties) || {}; return q._ring_builder === 'true' && !!q['Engraving Text'] && !q['Paired Setting'] && !q['Paired Diamond']; }
  // Diamond-shaped lines only (excludes setting + engraving-fee lines, which can carry _diamond_sku too).
  function matchesDiamondSku(li, sku) {
    if (!sku || isSettingLine(li) || isFeeLine(li)) return false;
    return (li.properties && li.properties._diamond_sku === sku) || li.sku === sku;
  }

  // ---- Robust REMOVE during the pending window ----
  // While a sentinel diamond is still being created, the theme's native remove targets the sentinel
  // line key — a loose line removes via GET /cart/change?id=<key> (can land on a Shopify error page),
  // and the in-flight reconcile would re-add the diamond after removal ("won't actually remove").
  // Intercept "Remove" / "Remove ring" here: resolve the line(s) from the CURRENT cart (diamonds by
  // SKU, the setting by its pairing attributes — stale DOM keys never error), remove via
  // /cart/update.js, then reload — which also tears down the in-flight reconcile.
  // INVARIANT (rbfix v1): never POST a removal that deletes only one member of a currently-consistent
  // bundle. If a member exists in the live cart but can't be confidently resolved, reload instead.
  document.addEventListener('click', function (e) {
    if (!pendingActive) return;
    var bundleRemove = e.target && e.target.closest && e.target.closest('.bundle__remove');
    var lineRemove = e.target && e.target.closest && e.target.closest('.cart__item .cart__remove a');
    if (!bundleRemove && !lineRemove) return;
    e.preventDefault(); e.stopImmediatePropagation();
    getCart().then(function (cart) {
      var items = cart.items || [];
      var updates = {};
      function findByKey(key) { if (!key) return null; for (var i = 0; i < items.length; i++) { if (items[i].key === key) return items[i]; } return null; }
      function findDiamond(sku, domKey) {
        if (!sku) return null;
        var m = items.filter(function (li) { return matchesDiamondSku(li, sku); });
        if (m.length > 1 && domKey) { for (var i = 0; i < m.length; i++) { if (m[i].key === domKey) return m[i]; } }
        return m[0] || null;
      }
      if (bundleRemove) {
        var bundle = bundleRemove.closest('.bundle');
        var settingEl = bundle && bundle.querySelector('.cart__item[data-bundle-type="setting"]');
        var diamondEl = bundle && bundle.querySelector('.cart__item[data-bundle-type="diamond"]');
        var sku = (diamondEl && diamondEl.getAttribute('data-diamond-sku')) || (settingEl && settingEl.getAttribute('data-setting-diamond-sku')) || null;
        if (diamondEl && !sku) { window.location.reload(); return; } // diamond card present but unresolvable
        var settingDomKey = settingEl && settingEl.getAttribute('data-key');
        // Setting: pairing attributes first, stale DOM key second, unique variant id last.
        var settingLine = null;
        var sMatches = sku ? items.filter(function (li) { return isSettingLine(li) && li.properties['Diamond SKU'] === sku; }) : [];
        if (sMatches.length === 1) settingLine = sMatches[0];
        else if (sMatches.length > 1) settingLine = sMatches.filter(function (li) { return li.key === settingDomKey; })[0] || null;
        if (!settingLine && !sMatches.length) {
          settingLine = findByKey(settingDomKey);
          if (!settingLine && settingDomKey) {
            var vid = parseInt(settingDomKey.split(':')[0], 10);
            var vMatches = vid ? items.filter(function (li) { return isSettingLine(li) && li.variant_id === vid; }) : [];
            if (vMatches.length === 1) settingLine = vMatches[0];
          }
        }
        var diamondLine = findDiamond(sku, diamondEl && diamondEl.getAttribute('data-key'));
        var diamondExists = sku ? items.some(function (li) { return matchesDiamondSku(li, sku); }) : false;
        if ((sMatches.length && !settingLine) || (diamondExists && !diamondLine) || (!settingLine && !diamondLine)) { window.location.reload(); return; }
        if (settingLine) updates[settingLine.key] = 0;
        if (diamondLine) updates[diamondLine.key] = 0;
        // Engraving fee scoped to THIS bundle: _diamond_sku stamp first, else exact Engraving Text
        // match on fee lines not stamped for another ring's diamond.
        var feeCands = items.filter(isFeeLine);
        var feeLine = sku ? (feeCands.filter(function (li) { return li.properties._diamond_sku === sku; })[0] || null) : null;
        if (!feeLine && settingLine && settingLine.properties['Custom Engraving']) {
          var text = settingLine.properties['Custom Engraving'];
          var byText = feeCands.filter(function (li) { return !li.properties._diamond_sku && li.properties['Engraving Text'] === text; });
          if (byText.length > 1) {
            var pill = bundle && bundle.querySelector('[data-engraving-pill]');
            var pillKey = pill && pill.getAttribute('data-fee-key');
            feeLine = byText.filter(function (li) { return li.key === pillKey; })[0] || byText[0];
          } else { feeLine = byText[0] || null; }
        }
        if (feeLine) updates[feeLine.key] = 0;
      } else {
        var it = lineRemove.closest('.cart__item[data-key]');
        var lineSku = it && it.getAttribute('data-diamond-sku');
        var domKey = it && it.getAttribute('data-key');
        var match = findDiamond(lineSku, domKey) || findByKey(domKey);
        if (!match) { window.location.reload(); return; }
        // A single-line remove must never split a currently-consistent bundle.
        var mp = match.properties || {};
        if (isSettingLine(match)) {
          var pairedSku = mp['Diamond SKU'];
          if (pairedSku && items.some(function (li) { return matchesDiamondSku(li, pairedSku); })) { window.location.reload(); return; }
        } else if (mp._ring_builder === 'true' && mp['Paired Setting']) {
          var mSku = mp._diamond_sku || match.sku;
          if (mSku && items.some(function (li) { return isSettingLine(li) && li.properties['Diamond SKU'] === mSku; })) { window.location.reload(); return; }
        }
        updates[match.key] = 0;
      }
      if (!Object.keys(updates).length) { window.location.reload(); return; }
      return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: updates }) })
        .then(function () { window.location.reload(); });
    }).catch(function () { window.location.reload(); });
  }, true);

  function pendingLines(cart) {
    return (cart.items || []).filter(function (li) { return li.properties && li.properties[PENDING_PROP] && li.variant_id === SENTINEL_VARIANT; });
  }

  // Reconcile one pending diamond line: create the real product, add it, remove the sentinel.
  function reconcile(line) {
    var p = line.properties;
    var rtype = p._ring_type || 'Ring'; // 'Ring' for Step-3, 'Loose' for Buy Loose
    return fetch(API_BASE + '/create-product?sku=' + encodeURIComponent(p._diamond_sku) + '&type=' + encodeURIComponent(rtype))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var realId = data.variant_id;
        if (!realId && !data.shopify_id) throw new Error('create-product returned no variant');
        if (!realId) throw new Error('no variant_id');
        // GUARD: the shopper may have REMOVED this pending line while create-product was running.
        // If the sentinel line is gone, do NOT re-add the real product (otherwise the diamond they
        // just removed "comes back"). Re-check the live cart before re-adding.
        return getCart().then(function (cart) {
          var stillThere = (cart.items || []).some(function (li) { return li.key === line.key; });
          if (!stillThere) { return { cancelled: true }; }
          // Carry forward the visible + linking properties (drop the _pending/_idempotency markers; keep _diamond_sku for hydration).
          var newProps = {};
          Object.keys(p).forEach(function (k) {
            if (k === PENDING_PROP || k === '_idempotency_key') return;
            newProps[k] = p[k];
          });
          newProps['_optimistic_swapped'] = '1';
          // ADD real first (cart never lacks the diamond), THEN remove sentinel.
          return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: realId, quantity: 1, properties: newProps }] }) })
            .then(function (r) { if (!r.ok) throw new Error('add real failed'); return r.json(); })
            .then(function () {
              // rbfix v1 (FIX 2b): the shopper may have hit "Remove ring" BETWEEN the stillThere
              // check and the add. Re-verify the ring's setting is still in the cart; if not,
              // remove the diamond we just added (plus any leftover sentinel) and skip finalize.
              if (rtype !== 'Ring') return null;
              return getCart().then(function (c2) {
                var items2 = c2.items || [];
                var hasSetting = items2.some(function (li) { return isSettingLine(li) && li.properties['Diamond SKU'] === p._diamond_sku; });
                if (hasSetting) return null;
                var added = null;
                for (var i = 0; i < items2.length; i++) {
                  var q = items2[i].properties || {};
                  if (q._optimistic_swapped === '1' && q._diamond_sku === p._diamond_sku) { added = items2[i]; break; }
                }
                var jobs = [];
                if (added) jobs.push(fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: added.key, quantity: 0 }) }).catch(function () {}));
                jobs.push(fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) }).catch(function () {}));
                return Promise.all(jobs).then(function () { return { cancelled: true }; });
              });
            })
            .then(function (guard) {
              if (guard && guard.cancelled) return guard;
              return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) })
                .then(function (r) { if (!r.ok) throw new Error('remove sentinel failed'); return r.json(); });
            });
        });
      });
  }

  function silentFinalize() {
    // NO re-render, NO reload. The placeholder line already shows the diamond correctly (image, price,
    // 4Cs, in its bundle) and now looks identical to the real line. Just quietly re-point each swapped
    // line's key to the real Shopify line so qty/remove keep working, then ungate. Zero visual change.
    return getCart().then(function (cart) {
      (cart.items || []).forEach(function (li) {
        if (!li.properties || !li.properties._optimistic_swapped || !li.properties._diamond_sku) return;
        var sku = li.properties._diamond_sku;
        var sel = '.cart__item[data-diamond-sku="' + (window.CSS && CSS.escape ? CSS.escape(sku) : sku) + '"]';
        var el = document.querySelector(sel);
        if (!el) return;
        el.setAttribute('data-key', li.key);
        var qi = el.querySelector('.js-qty__num'); if (qi) { qi.setAttribute('data-id', li.key); qi.id = 'cart_updates_' + li.key; }
        var rm = el.querySelector('.cart__remove a'); if (rm) rm.setAttribute('href', '/cart/change?id=' + encodeURIComponent(li.key) + '&quantity=0');
      });
    }).catch(function () {});
  }

  /* ================= rbfix v1: FIX 4 — bundle consistency validation + self-heal ================= */
  var IS_CART_PAGE = /\/cart(\b|\/|$)/.test(window.location.pathname);
  var REPAIR_TS_KEY = 'diy_rbfix_repaired_at';
  function recentlyRepaired() {
    try { var t = parseInt(sessionStorage.getItem(REPAIR_TS_KEY) || '0', 10); return !!t && (Date.now() - t) < 20000; } catch (err) { return false; }
  }
  function markRepaired() { try { sessionStorage.setItem(REPAIR_TS_KEY, String(Date.now())); } catch (err) {} }

  // Mismatched = setting lines whose 'Diamond SKU' has no diamond-shaped line left in the cart.
  function mismatchedSettings(items) {
    return items.filter(function (li) {
      if (!isSettingLine(li)) return false;
      var sku = li.properties['Diamond SKU'];
      if (!sku) return false;
      return !items.some(function (cand) { return matchesDiamondSku(cand, sku); });
    });
  }

  // UNAMBIGUOUS repair: exactly one mismatched setting + exactly one settled ring-builder diamond
  // not referenced by any other setting -> re-point the setting's pairing props at that diamond
  // via /cart/change.js, preserving every other property (Ship by stays untouched).
  // Resolves true only when the repair POST succeeded. Guarded so it can't loop.
  function healIfUnambiguous(items) {
    var broken = mismatchedSettings(items);
    if (broken.length !== 1 || recentlyRepaired()) return Promise.resolve(false);
    var setting = broken[0];
    var referenced = {};
    items.forEach(function (li) {
      if (isSettingLine(li) && li.key !== setting.key && li.properties['Diamond SKU']) referenced[li.properties['Diamond SKU']] = true;
    });
    var candidates = items.filter(function (li) {
      var q = li.properties || {};
      if (q._ring_builder !== 'true' || !q['Paired Setting'] || q[PENDING_PROP]) return false;
      var dsku = q._diamond_sku || li.sku;
      return !!dsku && !referenced[dsku];
    });
    if (candidates.length !== 1) return Promise.resolve(false);
    var d = candidates[0];
    var dq = d.properties || {};
    var dsku = dq._diamond_sku || d.sku;
    var summary = dq['Diamond'] || ((dq._carat || '') + 'ct ' + (dq._color || '') + ' ' + (dq._clarity || '') + ' ' + (dq._shape || ''));
    var newProps = {};
    Object.keys(setting.properties || {}).forEach(function (k) { newProps[k] = setting.properties[k]; });
    newProps['Paired Diamond'] = summary;
    newProps['Diamond SKU'] = dsku;
    markRepaired();
    console.log('[optimistic-cart] rbfix: re-pairing setting to in-cart diamond', dsku);
    return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: setting.key, quantity: setting.quantity, properties: newProps }) })
      .then(function (r) { return r.ok; })
      .catch(function () { return false; });
  }

  // On /cart init (only once all sentinels are settled — never while reconcile is running):
  // heal an unambiguous mismatch, then refresh the render so the bundle re-pairs. Ambiguous
  // carts are left alone here; the checkout-click gate below warns once instead.
  function validateOnInit(cart) {
    var items = cart.items || [];
    if (pendingLines(cart).length) return;
    if (!mismatchedSettings(items).length) return;
    healIfUnambiguous(items).then(function (repaired) {
      if (repaired) window.location.reload();
    });
  }

  // Checkout-click validation (capture; registered AFTER the pending gate above so the gate wins
  // during the pending window). A consistent cart passes straight through; an unambiguous mismatch
  // is repaired and checkout proceeds; an ambiguous one blocks ONCE with an inline notice and any
  // later click goes through untouched (never trap a shopper — bias toward allowing checkout).
  var checkoutChecked = false;
  function showBundleNotice(btn) {
    var n = document.getElementById('diy-bundle-notice');
    if (!n) {
      n = document.createElement('div');
      n.id = 'diy-bundle-notice';
      n.setAttribute('role', 'alert');
      n.style.cssText = 'margin:12px 0 0;padding:12px 14px;border:1px solid #b94a36;border-radius:3px;background:#faf1ef;color:#8f3a2a;font-size:13px;line-height:1.5;';
      n.textContent = 'One of the rings in your bag looks incomplete. Please review your bag and remove the extra ring item(s), then continue to checkout.';
    }
    var host = (btn && btn.parentNode) ? btn : checkoutBtn();
    if (host && host.parentNode) host.parentNode.insertBefore(n, host.nextSibling);
  }
  document.addEventListener('click', function (e) {
    if (!IS_CART_PAGE || pendingActive || checkoutChecked) return;
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"], .cart__checkout');
    if (!btn) return;
    // Ring-size-gated page: don't intercept (and don't spin) — the form's submit guard
    // blocks checkout and lights up the red "Please select a ring size." validation.
    var pageForm = document.getElementById('CartPageForm');
    if (pageForm && pageForm.classList.contains('is-size-gated')) return;
    e.preventDefault(); e.stopImmediatePropagation();
    btn.classList.add('diy-loading');
    getCart().then(function (cart) {
      checkoutChecked = true;
      btn.classList.remove('diy-loading');
      var items = cart.items || [];
      // Stale ungated page (bfcache restore / second tab): reload so the gate re-renders.
      if (cartNeedsRingSize(cart)) { window.location.reload(); return; }
      if (pendingLines(cart).length) { btn.click(); return; } // the reconcile flow owns pending carts
      if (!mismatchedSettings(items).length) { btn.click(); return; }
      healIfUnambiguous(items).then(function (repaired) {
        if (repaired) { btn.click(); return; }
        showBundleNotice(btn); // block once; the next click goes through
      });
    }).catch(function () { checkoutChecked = true; btn.classList.remove('diy-loading'); btn.click(); }); // fail open
  }, true);

  function initCartPage() {
    console.log('[optimistic-cart] rbfix v1');
    getCart().then(function (cart) {
      var pend = pendingLines(cart);
      if (!pend.length) { setGate(false); validateOnInit(cart); return; }
      setGate(true); // pending — checkout clicks now show an in-button spinner instead of navigating
      // The order summary (subtotal/discount/total) is corrected by main-cart's updateSummary(), which
      // adds each pending line's _real_price to cart.total_price (the sentinel is $0). It runs on load.
      Promise.all(pend.map(function (line) { return reconcile(line).catch(function (e) { console.error('[optimistic] reconcile failed', e); return { __err: true }; }); }))
        .then(function (results) {
          var anyErr = results.some(function (r) { return r && r.__err; });
          if (anyErr) {
            // Could not confirm the diamond. Stop the spinner and don't proceed to a $0 checkout.
            var btn = checkoutBtn(); if (btn) btn.classList.remove('diy-loading');
            if (wantsCheckout) { wantsCheckout = false; window.alert('We had trouble confirming your diamond. Please refresh and try again.'); }
            return; // pendingActive stays true so checkout remains intercepted
          }
          return silentFinalize().then(function () {
            document.dispatchEvent(new CustomEvent('cart:updated')); // recompute summary against the now-real cart
            setGate(false); // ungate + auto-proceed if the shopper was waiting at checkout
          });
        });
    }).catch(function (e) { console.error('[optimistic] init failed', e); setGate(false); });
  }

  // ── Ring-builder dedupe helpers (used by complete-ring's add-vs-update decision) ──
  // A shopper can't hold two of the same diamond. Collect EVERY cart line tied to `sku`
  // so the caller can fully remove the old ring/loose before re-adding (no orphans, and
  // robust to the reconcile add-then-remove window where two diamond lines coexist briefly).
  // Returns null, or { type:'ring'|'loose', diamondKey, settingKey, feeKey, settingVariantId, keys:[...] }.
  function findDiamondInCart(cart, sku) {
    var items = (cart && cart.items) || [];
    var diamondKeys = [], settingKeys = [], feeKeys = [];
    var settingVariantId = null, isRing = false, isLoose = false;
    var engravingTexts = [];
    // Pass 1: diamond lines (ring or loose) + setting lines for this SKU.
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      var p = li.properties || {};
      if ((li.sku && li.sku === sku) || p._diamond_sku === sku) {
        if (p._ring_builder === 'true' && (p['Paired Setting'] || p._ring_type === 'Ring')) { isRing = true; }
        else { isLoose = true; }
        diamondKeys.push(li.key);
        continue;
      }
      if (p._ring_builder === 'true' && p['Paired Diamond'] && p['Diamond SKU'] === sku) {
        isRing = true;
        settingKeys.push(li.key);
        if (settingVariantId == null) settingVariantId = li.variant_id;
        if (p['Custom Engraving']) engravingTexts.push(p['Custom Engraving']);
      }
    }
    if (!diamondKeys.length && !settingKeys.length) return null;
    // Pass 2: engraving-fee lines — match by _diamond_sku (precise) or, for legacy lines that
    // predate that stamp, by this ring's engraving text.
    for (var k = 0; k < items.length; k++) {
      var fp = items[k].properties || {};
      if (fp._ring_builder !== 'true') continue;
      if (fp['Paired Diamond'] || fp['Paired Setting'] || !fp['Engraving Text']) continue;
      if (fp._diamond_sku === sku || (engravingTexts.length && engravingTexts.indexOf(fp['Engraving Text']) !== -1)) {
        feeKeys.push(items[k].key);
      }
    }
    var keys = diamondKeys.concat(settingKeys).concat(feeKeys);
    return {
      type: isRing ? 'ring' : 'loose',
      diamondKey: diamondKeys[0] || null,
      settingKey: settingKeys[0] || null,
      feeKey: feeKeys[0] || null,
      settingVariantId: settingVariantId,
      keys: keys
    };
  }

  // Remove cart lines by key (quantity 0 via /cart/update.js — keeps other lines untouched).
  function removeCartLines(keys) {
    if (!keys || !keys.length) return Promise.resolve();
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates: updates }) })
      .then(function (r) { if (!r.ok) throw new Error('remove lines failed ' + r.status); return r.json(); });
  }

  /* ---- Ring-size gate helpers (shared by cart page + header drawer) ---- */
  function isSettingLineItem(li) {
    var p = li.properties || {};
    return p._ring_builder === 'true' && !!p['Paired Diamond'];
  }
  // Lines that must carry a Ring Size before checkout: builder setting lines, plus any
  // line stamped `_needs_ring_size` (wedding bands added sizeless from the PDP/quick-view).
  function ringSizeMissingLines(cart) {
    return (cart.items || []).filter(function (li) {
      var p = li.properties || {};
      var sizable = isSettingLineItem(li) || p._needs_ring_size === 'true';
      return sizable && !(p['Ring Size'] && String(p['Ring Size']).replace(/\s+/g, ''));
    });
  }
  function cartNeedsRingSize(cart) { return ringSizeMissingLines(cart).length > 0; }
  // Poll until no line is mid-reconcile (_pending_diamond_id) or the deadline passes.
  // Mutating a pending sentinel's properties re-keys the line under reconcile()'s feet
  // (reconcile resolves the sentinel by its captured line.key), which can duplicate the
  // real diamond or orphan the sentinel — so size stamping always waits for a settled cart.
  function waitForNoPending(maxMs) {
    var deadline = Date.now() + (maxMs || 20000);
    function poll() {
      return getCart().then(function (cart) {
        var pending = (cart.items || []).some(function (li) { return li.properties && li.properties._pending_diamond_id; });
        if (!pending || Date.now() > deadline) return cart;
        return new Promise(function (res) { setTimeout(res, 700); }).then(poll);
      });
    }
    return poll();
  }
  // Self-detect /cart page.
  function onReady(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
  if (/\/cart(\b|\/|$)/.test(window.location.pathname)) { onReady(initCartPage); }

  window.DiyonaOptimisticCart = {
    SENTINEL_VARIANT: SENTINEL_VARIANT,
    optimisticAddRing: optimisticAddRing,
    optimisticAddLoose: optimisticAddLoose,
    buildRingLineItems: buildRingLineItems,
    buildLooseLineItem: buildLooseLineItem,
    showAtcAnimation: showAtcAnimation,
    hideAtcAnimation: hideAtcAnimation,
    findDiamondInCart: findDiamondInCart,
    removeCartLines: removeCartLines,
    ringSizeMissingLines: ringSizeMissingLines,
    cartNeedsRingSize: cartNeedsRingSize,
    waitForNoPending: waitForNoPending,
    _initCartPage: initCartPage
  };
})();
